import { Children, cloneElement, forwardRef, useImperativeHandle, useRef } from 'react'

export default forwardRef(function Accordion(
  { title, open, adornment = null, name, onToggle, children },
  ref,
) {
  const titleRef = useRef()

  useImperativeHandle(
    ref,
    () => ({
      scrollIntoView: (...args) => titleRef.current.scrollIntoView(...args),
    }),
    [],
  )

  return (
    <div className='accordion'>
      <div className='title' ref={titleRef}>
        <button className='flatBtn' onClick={() => onToggle(name, !open)}>
          {title}
        </button>
        {adornment === null ? (
          <svg width='10.281' height='5.141' viewBox='0 0 10.281 5.141' className='arrow'>
            <path d='M10.281,5.141H0L5.141,0Z' />
          </svg>
        ) : (
          <div className='adornment'>{adornment}</div>
        )}
      </div>
      <div className='children'>{cloneElement(Children.only(children), { open })}</div>

      <style jsx>{`
        .accordion {
          position: relative;
          width: 100%;
        }

        .title {
          position: relative;
          overflow: hidden;
          padding: 0.3rem 0.5rem 0.8rem;

          button {
            width: 100%;
            padding: 2.2rem 7rem 2.2rem 2.9rem;
            text-align: left;
            outline: none;
            background: var(--secondary-bg-color);
            border-radius: 1rem;
            box-shadow: 0 0.3rem 0.6rem rgba(0, 0, 0, 0.16);
            font-weight: 700;
            font-size: 2.4rem;
            color: var(--primary-text-color);

            &:focus {
              color: var(--primary-color);

              + svg.arrow {
                fill: var(--primary-color);
              }
            }
          }

          svg.arrow {
            position: absolute;
            top: 50%;
            right: 3.6rem;
            fill: var(--primary-text-color);
            transition: transform 0.2s;
          }

          .adornment {
            position: absolute;
            top: 50%;
            right: 3.6rem;
            transform: translate(0, -67%);
          }
        }

        .children {
          padding: 2.6rem 2rem 3.5rem;
          opacity: 0;
          transition: opacity 0.3s, transform 0.3s;
        }

        @media (max-width: 1024px) {
          .title {
            button {
              padding: 1rem 4rem 1rem 1.3rem;
              font-size: 1.4rem;
            }

            svg.arrow {
              right: 2rem;
            }

            .adornment {
              right: 2rem;
            }
          }

          .children {
            padding: 1rem 1.5rem 2.2rem;
          }
        }
      `}</style>

      <style jsx>{`
        .accordion {
          min-height: ${open ? 'auto' : '8.6rem'};
        }

        .title {
          svg.arrow {
            transform: ${open
              ? 'translate(0, -50%) rotate(0)'
              : 'translate(0, -50%) rotate(-180deg)'};
          }
        }

        .children {
          opacity: ${open ? 1 : 0};
          visibility: ${open ? 'visible' : 'hidden'};
          ${!open ? 'display: none;' : null}
          position: ${open ? 'static' : 'absolute'};
          top: ${open ? 'auto' : '-5000px'};
        }

        @media (max-width: 1024px) {
          .accordion {
            min-height: ${open ? 'auto' : '5.2rem'};
          }
        }
      `}</style>
    </div>
  )
})
